import React, { useEffect, useState, useRef } from 'react';
import { Plus, Trash2, Pencil, Download, Image as ImageIcon, X, Upload } from 'lucide-react';
import axios from 'axios';
import { BACKEND_URL, apiErr } from '../shared';

// ─────────────────────────────────────────────────────────────────────────────
// AddonsTab
// ─────────────────────────────────────────────────────────────────────────────
// Las categorías las manda el backend (/api/addon-categories); `id` es lo que se guarda.
const EMPTY_ADDON_FORM = { name: '', description: '', version: '', category: 'utilidades', download_url: '', image_url: '', required: false, launcher: false };

const formatMb = (bytes) => `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;

function ZipInfo({ addon }) {
  const info = addon.zip_info;
  const small = { fontSize: 11, margin: '3px 0 0' };
  if (!info) {
    return <p style={{ ...small, color: 'rgba(224,247,255,0.3)' }}>Sin zip en Frostbound (enlace externo): no sale en el launcher.</p>;
  }
  if (info.error) {
    return <p style={{ ...small, color: '#f87171' }}>Zip no válido: {info.error}</p>;
  }
  return (
    <p style={{ ...small, color: 'rgba(224,247,255,0.45)' }}>
      Zip: {info.folders.join(', ')}{info.root ? ` (dentro de ${info.root}/)` : ''}
      {' · '}{info.version ? `v${info.version}` : `rev. ${info.sha256.slice(0, 7)}`}
      {' · '}{formatMb(info.size)}
    </p>
  );
}

export default function AddonsTab({ showFeedback }) {
  const [addons, setAddons]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showForm, setShowForm]     = useState(false);
  const [editingId, setEditingId]   = useState(null);
  const [form, setForm]             = useState(EMPTY_ADDON_FORM);
  const [saving, setSaving]         = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [seeding, setSeeding]       = useState(false);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [categories, setCategories] = useState([]);
  const [uploadingZipId, setUploadingZipId] = useState(null);
  const imgInputRef = useRef(null);
  const zipInputRef = useRef(null);
  const zipTargetRef = useRef(null);

  const fetchAddons = () => {
    setLoading(true);
    axios.get(`${BACKEND_URL}/api/admin/addons`, { withCredentials: true })
      .then(({ data }) => setAddons(data))
      .catch(() => showFeedback('err', 'Error al cargar addons'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchAddons(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/addon-categories`)
      .then(({ data }) => setCategories(data))
      .catch(() => showFeedback('err', 'Error al cargar las categorías'));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const categoryLabel = (id) => categories.find(c => c.id === id)?.label ?? id;

  const pickZip = (addon) => {
    zipTargetRef.current = addon;
    zipInputRef.current?.click();
  };

  const handleUploadZip = async (e) => {
    const file = e.target.files?.[0];
    const addon = zipTargetRef.current;
    e.target.value = '';
    if (!file || !addon) return;
    const fd = new FormData();
    fd.append('file', file);
    setUploadingZipId(addon._id);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/admin/addons/${addon._id}/zip`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      showFeedback('ok', `Zip de ${addon.name} subido: ${data.folders.join(', ')} · ${data.version_display}.`);
      fetchAddons();
    } catch (err) {
      showFeedback('err', apiErr(err, 'Error al subir el zip'));
    } finally {
      setUploadingZipId(null);
    }
  };

  const handleSeed = async () => {
    if (!window.confirm('¿Importar los 8 addons por defecto? Solo funciona si la lista está vacía.')) return;
    setSeeding(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/admin/addons/seed`, {}, { withCredentials: true });
      showFeedback('ok', `Importados ${data.inserted} addons correctamente.`);
      fetchAddons();
    } catch (err) {
      showFeedback('err', apiErr(err, 'Error al importar'));
    } finally {
      setSeeding(false);
    }
  };

  const handleUploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    const fd = new FormData();
    fd.append('file', file);
    setUploadingImg(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/admin/upload-image`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setForm(f => ({ ...f, image_url: data.url }));
      showFeedback('ok', 'Imagen subida.');
    } catch {
      showFeedback('err', 'Error al subir la imagen.');
    } finally {
      setUploadingImg(false);
    }
  };

  const handleEdit = (a) => {
    setEditingId(a._id);
    setForm({ name: a.name, description: a.description || '', version: a.version || '', category: a.category, download_url: a.download_url || '', image_url: a.image_url || '', required: a.required || false, launcher: a.launcher || false });
    setShowForm(true);
  };

  const handleCancel = () => { setShowForm(false); setEditingId(null); setForm(EMPTY_ADDON_FORM); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${BACKEND_URL}/api/admin/addons/${editingId}`, form, { withCredentials: true });
        showFeedback('ok', 'Addon actualizado.');
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/addons`, form, { withCredentials: true });
        showFeedback('ok', 'Addon añadido.');
      }
      handleCancel();
      fetchAddons();
    } catch (err) {
      showFeedback('err', apiErr(err, 'Error al guardar'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este addon?')) return;
    setDeletingId(id);
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/addons/${id}`, { withCredentials: true });
      showFeedback('ok', 'Addon eliminado.');
      setAddons(prev => prev.filter(a => a._id !== id));
    } catch {
      showFeedback('err', 'Error al eliminar.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="admin-section anim-fade-up" style={{ animationDelay: '0.15s' }}>
      <div className="admin-section-header" style={{ flexWrap: 'wrap', gap: 8 }}>
        <h2 className="admin-section-title">Gestión de Addons ({addons.length})</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          {addons.length === 0 && (
            <button className="admin-create-btn" onClick={handleSeed} disabled={seeding}
              style={{ background: 'rgba(255,140,0,0.15)', borderColor: 'rgba(255,140,0,0.5)', color: '#ffaa44' }}>
              <Download size={14} /> {seeding ? 'Importando...' : 'Importar addons por defecto'}
            </button>
          )}
          <button className="admin-create-btn" onClick={() => showForm ? handleCancel() : setShowForm(true)}>
            {showForm ? <><X size={14} /> Cancelar</> : <><Plus size={14} /> Nuevo Addon</>}
          </button>
        </div>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="admin-form">
          <p className="admin-form-mode-label">{editingId ? '✏️ Editando addon' : '➕ Nuevo addon'}</p>

          <div className="admin-form-row">
            <div className="form-field" style={{ flex: 2 }}>
              <label htmlFor="ap-addon-name" className="form-label">NOMBRE</label>
              <div className="input-wrap">
                <input id="ap-addon-name" type="text" className="form-input" style={{ paddingLeft: 14 }}
                  placeholder="Nombre del addon" value={form.name} required
                  onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))} />
              </div>
            </div>
            <div className="form-field" style={{ flex: 1, minWidth: 100 }}>
              <label htmlFor="ap-addon-version" className="form-label">VERSIÓN</label>
              <div className="input-wrap">
                <input id="ap-addon-version" type="text" className="form-input" style={{ paddingLeft: 14 }}
                  placeholder="Se rellena al subir el zip" value={form.version}
                  onChange={e => setForm(prev => ({ ...prev, version: e.target.value }))} />
              </div>
            </div>
            <div className="form-field" style={{ flex: 1, minWidth: 130 }}>
              <label htmlFor="ap-addon-category" className="form-label">CATEGORÍA</label>
              <select id="ap-addon-category" className="form-input form-select" style={{ paddingLeft: 14 }}
                value={form.category} onChange={e => setForm(prev => ({ ...prev, category: e.target.value }))}>
                {categories.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
          </div>

          <div className="form-field">
            <label htmlFor="ap-addon-desc" className="form-label">DESCRIPCIÓN</label>
            <div className="input-wrap">
              <input id="ap-addon-desc" type="text" className="form-input" style={{ paddingLeft: 14 }}
                placeholder="Descripción breve del addon" value={form.description}
                onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))} />
            </div>
          </div>

          <div className="admin-form-row">
            <div className="form-field" style={{ flex: 1 }}>
              <label htmlFor="ap-addon-url" className="form-label">URL DE DESCARGA</label>
              <div className="input-wrap">
                <input id="ap-addon-url" type="text" className="form-input" style={{ paddingLeft: 14 }}
                  placeholder="Se rellena al subir el zip (o https://... externo)" value={form.download_url}
                  onChange={e => setForm(prev => ({ ...prev, download_url: e.target.value }))} />
              </div>
            </div>
            <div className="form-field" style={{ minWidth: 140, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
              <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', paddingBottom: 8 }}>
                <input type="checkbox" checked={form.required}
                  onChange={e => setForm(prev => ({ ...prev, required: e.target.checked }))} />
                <span>RECOMENDADO</span>
              </label>
              <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', paddingBottom: 8 }}
                title="Aparece en la vista Addons del Frostbound Launcher (necesita un zip subido aquí)">
                <input type="checkbox" checked={form.launcher}
                  onChange={e => setForm(prev => ({ ...prev, launcher: e.target.checked }))} />
                <span>MOSTRAR EN EL LAUNCHER</span>
              </label>
            </div>
          </div>

          {/* Imagen */}
          <div className="form-field">
            <label htmlFor="ap-addon-img" className="form-label">IMAGEN DEL ADDON</label>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <div className="input-wrap" style={{ flex: 1 }}>
                <input id="ap-addon-img" type="text" className="form-input" style={{ paddingLeft: 14 }}
                  placeholder="URL de imagen o sube un archivo"
                  value={form.image_url}
                  onChange={e => setForm(prev => ({ ...prev, image_url: e.target.value }))} />
              </div>
              <input type="file" accept="image/*" ref={imgInputRef} style={{ display: 'none' }} onChange={handleUploadImage} />
              <button type="button" onClick={() => imgInputRef.current?.click()} disabled={uploadingImg}
                style={{ background: 'rgba(0,229,255,0.08)', border: '1px solid rgba(0,229,255,0.25)', borderRadius: 7,
                  color: '#00e5ff', fontSize: 11, fontWeight: 700, padding: '8px 14px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                <ImageIcon size={13} style={{ marginRight: 5, verticalAlign: 'middle' }} />
                {uploadingImg ? 'Subiendo...' : 'Subir imagen'}
              </button>
              {form.image_url && (
                <img src={form.image_url.startsWith('/') ? `${BACKEND_URL}${form.image_url}` : form.image_url}
                  alt="preview" style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 6, border: '1px solid rgba(0,229,255,0.2)' }}
                  onError={e => { e.target.style.display = 'none'; }} />
              )}
            </div>
          </div>

          <button type="submit" className="admin-create-btn" disabled={saving}
            style={{ alignSelf: 'flex-start' }}>
            {saving ? 'Guardando...' : editingId ? 'Actualizar Addon' : 'Crear Addon'}
          </button>
        </form>
      )}

      <input type="file" accept=".zip,application/zip" ref={zipInputRef} style={{ display: 'none' }} onChange={handleUploadZip} />

      {loading ? (
        <p style={{ color: 'rgba(0,229,255,0.4)', fontSize: 13 }}>Cargando...</p>
      ) : addons.length === 0 ? (
        <div className="armory-empty">No hay addons. Crea el primero.</div>
      ) : (
        <div className="admin-news-list">
          {addons.map(a => (
            <div key={a._id} className="admin-news-item">
              <div className="admin-news-item-left">
                <div>
                  <p className="admin-news-item-title">{a.name}
                    {a.version && <span style={{ color: 'rgba(224,247,255,0.35)', fontWeight: 400, marginLeft: 8, fontSize: 11 }}>v{a.version}</span>}
                    {a.required && <span className="news-category-badge" style={{ marginLeft: 8, background: 'rgba(74,222,128,0.1)', border: '1px solid rgba(74,222,128,0.35)', color: '#4ade80', fontSize: 10 }}>RECOMENDADO</span>}
                    {a.launcher && <span className="news-category-badge" style={{ marginLeft: 8, fontSize: 10 }}>LAUNCHER</span>}
                  </p>
                  <p style={{ color: 'rgba(224,247,255,0.4)', fontSize: 12, margin: '2px 0 0' }}>{categoryLabel(a.category)} · {a.description}</p>
                  <ZipInfo addon={a} />
                </div>
              </div>
              <div className="admin-news-item-actions">
                <button className="admin-edit-btn" onClick={() => pickZip(a)} disabled={uploadingZipId !== null}
                  title="Subir zip (sustituye al actual y guarda una copia)">
                  {uploadingZipId === a._id ? '…' : <Upload size={13} />}
                </button>
                <button className="admin-edit-btn" onClick={() => handleEdit(a)}><Pencil size={13} /></button>
                <button className="admin-delete-btn" onClick={() => handleDelete(a._id)} disabled={deletingId === a._id}>
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
